import * as Sentry from "@sentry/react";
import { useEffect, useRef } from "react";
import { useQuery } from "react-query";

import {
  ENTITIES_ON_MAP_QUERY_KEY,
  ENTITIES_ON_MAP_STALE_TIME,
  MAP_QUERY_CACHE_TIME,
  fetchEntitiesOnMap
} from "api/map";

import useShouldFetchEntitiesOnMap from "components/map/hooks/useShouldFetchEntitiesOnMap";

/**
 * Stable identity so a repeated clear is recognised as already delivered rather
 * than pushed into the caller again.
 */
const NO_GROUPS = {};

export default function useGetGroupedEntities(
  txnId: Maybe<string>,
  handlePostData: (postData) => void,
  handleError: (err) => void = null,
  // When false, ignore the 2D map widget's zoom/well-count fetch policy. The
  // policy is keyed on the map widget's own state, so an unrelated widget on the
  // same dashboard (e.g. ChartDashboard) would otherwise have its entity data
  // silently cleared whenever the map is zoomed out. Same rationale as the
  // explicit opt-out in XdaViewer/ThreeDViewer.
  respectMapFetchPolicy = true
) {
  const mapFetchPolicyAllows = useShouldFetchEntitiesOnMap();
  const shouldFetchEntitiesOnMap = respectMapFetchPolicy ? mapFetchPolicyAllows : true;

  const { isLoading, isError, error, data, refetch } = useQuery(
    [ENTITIES_ON_MAP_QUERY_KEY, txnId],
    async () => {
      if (!txnId) return null;
      return await fetchEntitiesOnMap(txnId);
    },
    {
      enabled: !!txnId && shouldFetchEntitiesOnMap,
      staleTime: ENTITIES_ON_MAP_STALE_TIME, // immutable per txnId — never refetch while active
      cacheTime: MAP_QUERY_CACHE_TIME, // shared across all entitiesOnMap observers — react-query takes the max
      onError: (err) => {
        if (handleError) {
          handleError(err);
        } else {
          Sentry.withScope((scope) => {
            scope.setTag("component", "useGetGroupedEntities");
            scope.setLevel("error");
            Sentry.captureException(err);
          });
        }
      }
    }
  );

  // handlePostData is often a non-memoized inline callback; calling it via a ref
  // (instead of listing it as a dependency) keeps this effect from re-firing every
  // time the caller re-renders and creates a new handlePostData reference.
  const handlePostDataRef = useRef(handlePostData);
  useEffect(() => {
    handlePostDataRef.current = handlePostData;
  }, [handlePostData]);

  // Deliver on the data, not on the fetch. react-query only fires onSuccess when
  // a request actually resolves, so a key whose answer is already cached
  // delivered nothing and left the caller holding the previous key's groups.
  // The fetch-policy clear shares this effect so the two cannot race: when the
  // policy forbids the fetch, the groups are emptied whatever the cache holds.
  //
  // `undefined` means nothing has resolved yet and there is nothing to say;
  // `null` is this query's own answer for a missing txnId and does clear the
  // groups, so it is mapped rather than swallowed. Delivering the same clear
  // twice is skipped, since another observer on this key refetching would
  // otherwise push an identical empty object at the caller each time.
  const lastDeliveredRef = useRef<unknown>(undefined);
  useEffect(() => {
    const next =
      txnId && !shouldFetchEntitiesOnMap
        ? NO_GROUPS
        : data === undefined
        ? undefined
        : data ?? NO_GROUPS;
    if (next === undefined || next === lastDeliveredRef.current) return;
    lastDeliveredRef.current = next;
    handlePostDataRef.current?.(next);
  }, [data, shouldFetchEntitiesOnMap, txnId]);

  return { isLoading, isError, error, data, refetch };
}
